/**
 * ``useComments`` — the review-comment threads of the active brief, for the
 * Comments tab. Mirrors `use-versions.ts`: reads the active context from
 * `useCaseStore` (no new store, W5), calls the `comments` port, returns
 * `{ threads, total, loading, loadingMore, hasMore, error, filter, setFilter,
 * addComment, resolveThread, reload, loadMore }`.
 *
 * Gated on an active brief: `reload()` is a no-op when no brief is bound
 * (a fresh, unbound doc has no thread list). The open/resolved filter is a
 * SERVER query (passed to `listThreads`), never a local computation (W1);
 * `setFilter` re-fetches. Paging is pure data orchestration (W1): `fetchWith`
 * fetches page 1 (offset 0) and REPLACES the list; `loadMore` fetches the next
 * page (offset = loaded count) and APPENDS it. `total` (the BE count) drives
 * `hasMore` — never a local computation. `addComment`/`resolveThread` mutate
 * then refetch page 1 so the list reflects the BE truth. No Office.js (W2).
 */

import { useCallback, useRef, useState } from 'react'
import { getContainer } from '@/container'
import { useCaseStore } from '@/ui/stores/use-case-store'
import type { CommentThread, ThreadFilter } from '@/domain/comments/entity'

/** Page size for the thread list. Mirrors the adapter's `PAGE_SIZE` default;
 *  kept local so the hook (ui/) never imports the http adapter (boundary). */
const PAGE_SIZE = 50

export interface UseCommentsResult {
  threads: CommentThread[]
  /** The BE's full count of threads for the active filter (a SERVER number). */
  total: number
  loading: boolean
  /** True while a `loadMore` page (offset > 0) is in flight. */
  loadingMore: boolean
  /** True when the accumulated list is shorter than `total` (more to load). */
  hasMore: boolean
  error: Error | null
  filter: ThreadFilter
  /** Switch the open/resolved/all filter and re-fetch page 1 from the BE. */
  setFilter: (filter: ThreadFilter) => Promise<void>
  /** Post a reply to a thread, then refetch page 1. */
  addComment: (threadId: string, body: string) => Promise<void>
  /** Resolve / re-open a thread, then refetch page 1. */
  resolveThread: (threadId: string, isResolved: boolean) => Promise<void>
  /** Fetch the active brief's page 1 for the current filter. No-op when no
   *  brief is active. */
  reload: () => Promise<void>
  /** Append the next page (offset = current loaded count). No-op when there is
   *  nothing more to load or a load is already in flight. */
  loadMore: () => Promise<void>
}

export function useComments(): UseCommentsResult {
  const [threads, setThreads] = useState<CommentThread[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [filter, setFilterState] = useState<ThreadFilter>('open')
  // Last-writer-wins guard: switching the filter (or a mutate-then-refetch)
  // can leave two `listThreads` in flight. We only apply / clear-loading for
  // the most recent request, so a slow older response can't overwrite a newer
  // one (e.g. an `open` response landing after the user already picked
  // `resolved`). The same seq also protects `loadMore` — a stale page-1
  // response must never clobber a newer filter's page, and a double-clicked
  // `loadMore` only applies the latest.
  const requestSeq = useRef(0)

  const hasMore = threads.length < total

  const fetchWith = useCallback(async (next: ThreadFilter) => {
    const active = useCaseStore.getState().activeContext
    if (!active) return
    const reqId = ++requestSeq.current
    setLoading(true)
    // Page 1 supersedes any in-flight loadMore: its write is already seq-gated
    // out, but clear the sibling flag here too — the stale loadMore's `finally`
    // sees a newer seq and skips its own clear, so without this the spinner
    // would wedge `true` and dead-lock the button until a full reload (MED-1).
    setLoadingMore(false)
    setError(null)
    try {
      const { comments } = getContainer()
      const page = await comments.listThreads(active.briefId, next, {
        offset: 0,
        limit: PAGE_SIZE,
      })
      if (reqId === requestSeq.current) {
        // Page 1 REPLACES the list.
        setThreads(page.threads)
        setTotal(page.total)
      }
    } catch (err: unknown) {
      const wrapped = err instanceof Error ? err : new Error(String(err))
      // eslint-disable-next-line no-console
      console.error('[useComments] load failed', wrapped)
      if (reqId === requestSeq.current) setError(wrapped)
    } finally {
      if (reqId === requestSeq.current) setLoading(false)
    }
  }, [])

  const loadMore = useCallback(async () => {
    const active = useCaseStore.getState().activeContext
    if (!active) return
    // Guard: nothing more to load, or a page is already in flight.
    if (loadingMore || threads.length >= total) return
    const reqId = ++requestSeq.current
    const offset = threads.length
    setLoadingMore(true)
    setError(null)
    try {
      const { comments } = getContainer()
      const page = await comments.listThreads(active.briefId, filter, {
        offset,
        limit: PAGE_SIZE,
      })
      if (reqId === requestSeq.current) {
        if (page.threads.length === 0) {
          // Empty page — the BE has nothing more. Clamp total to the loaded
          // count so `hasMore` goes false, instead of leaving a dead-end "load
          // more" that re-fetches an empty page on every click (a stale-high
          // total would otherwise keep `threads.length < total` true forever).
          setTotal(offset)
        } else {
          // APPEND, deduping by id so a double-dispatch can't double-append.
          setThreads((prev) => {
            const seen = new Set(prev.map((th) => th.id))
            return [...prev, ...page.threads.filter((th) => !seen.has(th.id))]
          })
          setTotal(page.total)
        }
      }
    } catch (err: unknown) {
      const wrapped = err instanceof Error ? err : new Error(String(err))
      // eslint-disable-next-line no-console
      console.error('[useComments] loadMore failed', wrapped)
      if (reqId === requestSeq.current) setError(wrapped)
    } finally {
      if (reqId === requestSeq.current) setLoadingMore(false)
    }
  }, [filter, loadingMore, threads.length, total])

  const reload = useCallback(() => fetchWith(filter), [fetchWith, filter])

  const setFilter = useCallback(
    async (next: ThreadFilter) => {
      setFilterState(next)
      await fetchWith(next)
    },
    [fetchWith],
  )

  const addComment = useCallback(
    async (threadId: string, body: string) => {
      const active = useCaseStore.getState().activeContext
      if (!active) return
      try {
        const { comments } = getContainer()
        await comments.addComment(active.briefId, threadId, body)
      } catch (err: unknown) {
        const wrapped = err instanceof Error ? err : new Error(String(err))
        // eslint-disable-next-line no-console
        console.error('[useComments] addComment failed', wrapped)
        setError(wrapped)
        return
      }
      await fetchWith(filter)
    },
    [fetchWith, filter],
  )

  const resolveThread = useCallback(
    async (threadId: string, isResolved: boolean) => {
      const active = useCaseStore.getState().activeContext
      if (!active) return
      try {
        const { comments } = getContainer()
        await comments.resolveThread(active.briefId, threadId, isResolved)
      } catch (err: unknown) {
        const wrapped = err instanceof Error ? err : new Error(String(err))
        // eslint-disable-next-line no-console
        console.error('[useComments] resolveThread failed', wrapped)
        setError(wrapped)
        return
      }
      await fetchWith(filter)
    },
    [fetchWith, filter],
  )

  return {
    threads,
    total,
    loading,
    loadingMore,
    hasMore,
    error,
    filter,
    setFilter,
    addComment,
    resolveThread,
    reload,
    loadMore,
  }
}
